// theme/src/gift-wrap/src/packaging/components/GiftPresentationStep.jsx
import React from 'react';
import Knob from '../../components/Knob';
import { useGiftWrapContext } from '../../context/GiftWrapContext';
import { useLocale } from '../../context/LocaleContext';
import { usePackaging } from '../PackagingContext';
import { plainVariant, nameVariant } from '../buildChoices';
import { PRESENTATION } from '../selection';
import { getPresentationCopy } from '../copy';
import { OptionCard } from './OptionCard';
import { SealPicker } from './SealPicker';
import { NameField } from './NameField';
import styles from '../packaging.module.css';

const priceOf = (option) => plainVariant(option)?.price ?? 0;

export function GiftPresentationStep() {
  const { selection, dispatch, groups, stepState } = usePackaging();
  const { setGiftInfo } = useGiftWrapContext();
  const { formatPrice } = useLocale();
  const { sealEligible, presentationOptions } = stepState;

  // Each seal is opt-in with its own switch; its letter defaults to the first stamp variant (Q9a).
  const seal = (key, stamp) => ({
    stamp,
    on: selection.sealsOn[key],
    onToggle: () => dispatch({ type: 'setSealOn', seal: key, on: !selection.sealsOn[key] }),
    value: selection.letters[key] ?? stamp?.variants?.[0]?.id ?? '',
    onChange: (variantId) => dispatch({ type: 'setLetter', seal: key, variantId })
  });
  const info = (option) => () =>
    setGiftInfo({ title: option.title, price: priceOf(option), imageUrl: option.imageUrl, description: option.description });
  const bagName = nameVariant(groups.bag);
  const namePrice = bagName ? Math.max(0, bagName.price - priceOf(groups.bag)) : 0;

  const cards = {
    [PRESENTATION.BAG]: {
      option: groups.bag,
      images: [groups.bag?.imageUrl],
      price: priceOf(groups.bag),
      sealed: sealEligible.bag,
      body: (
        <>
          {sealEligible.bag && (
            <SealPicker
              id="seal-bag"
              title="Add wax seal initial"
              label="Choose Initial"
              {...seal('bag', groups.stampBag)}
            />
          )}
          {bagName && (
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                {/* Knob ignores aria-label; it labels its hidden checkbox through `id`. */}
                <label htmlFor="bag-name-toggle" className={styles.field}>
                  <span className={styles.fieldLabel}>Add their name</span>
                  <span className={styles.helper}>Personalised on the front of the bag</span>
                </label>
                <span>+{formatPrice({ amount: namePrice })}</span>
                <Knob
                  id="bag-name-toggle"
                  isEnabled={selection.bagNameOn}
                  onChange={() => dispatch({ type: 'setBagNameOn', on: !selection.bagNameOn })}
                />
              </div>
              {selection.bagNameOn && (
                <NameField id="bag-name" label="Enter Name" value={selection.bagName} onChange={(text) => dispatch({ type: 'setBagName', text })} />
              )}
            </div>
          )}
        </>
      )
    },
    [PRESENTATION.KEEPSAKE]: {
      option: groups.keepsake,
      images: [groups.keepsake?.imageUrl],
      price: priceOf(groups.keepsake),
      sealed: sealEligible.box,
      body: (
        <>
          <div className={styles.panel}>
            <NameField
              id="lid-name"
              label="Name on the lid"
              helper="Included"
              value={selection.lidName}
              onChange={(text) => dispatch({ type: 'setLidName', text })}
            />
          </div>
          {sealEligible.box && (
            <SealPicker
              id="seal-keepsake"
              title="Add wax seal initial"
              label="Choose Initial"
              {...seal('box', groups.stampBox)}
            />
          )}
        </>
      )
    },
    [PRESENTATION.PAIR]: {
      option: groups.bag,
      images: [groups.bag?.imageUrl, groups.box?.imageUrl],
      price: priceOf(groups.bag) + priceOf(groups.box),
      sealed: sealEligible.bag || sealEligible.box,
      body: (
        <>
          {sealEligible.bag && (
            <SealPicker
              id="seal-pair-bag"
              title="Add wax seal initial"
              helper="On the gift bag"
              label="Choose Initial (bag seal)"
              {...seal('bag', groups.stampBag)}
            />
          )}
          {sealEligible.box && (
            <SealPicker
              id="seal-pair-box"
              title="Add wax seal initial"
              helper="On the gift box"
              label="Choose Initial (box seal)"
              {...seal('box', groups.stampBox)}
            />
          )}
        </>
      )
    }
  };

  return (
    <div role="radiogroup" aria-label="Gift presentation" className={styles.cards}>
      {presentationOptions.map((kind) => {
        const card = cards[kind];
        const copy = getPresentationCopy(kind);
        return (
          <OptionCard
            key={kind}
            name={copy.name}
            price={card.price}
            heroImages={card.images.filter(Boolean)}
            ticks={copy.ticks({ sealed: card.sealed })}
            selected={selection.presentation === kind}
            onSelect={() => dispatch({ type: 'togglePresentation', presentation: kind })}
            onInfo={info(card.option)}
          >
            {card.body}
          </OptionCard>
        );
      })}
    </div>
  );
}
